import React, { useEffect, useState } from "react";
import { Alert, Box, Checkbox, FormControlLabel } from "@mui/material";
import { Button } from "react-bootstrap";
import { useTranslation } from "react-i18next";
import {
  fetchUpdateUserMeataData,
  upsertAccountAndSyncSF,
} from "../../../utils/helpers/api";
import { useNotificationTimestamp } from "../../../hooks/features/notification/useNotificationTimestamp";
import { Http } from "../../../utils/constants";
import { AxiosResponse } from "axios";
import { useGlobalState } from "../../../contexts/GlobalContext";
import { useSearchParams } from "react-router-dom";
import {
  occupationSelect,
  PERSON_TITLE,
  positions,
  workAddressSelect,
  businessAndManagementJobs,
  creativeAndDesignJobs,
  environmentAndInfrastructureJobs,
  generalOrganizationAndSkillsKnowledge,
  generalSocialTrendsCode,
  generalWorkAndLifePlanning,
  informationAndTechnologyJobs1,
  informationAndTechnologyJobs2,
  other,
  environmentalManagementJobs,
  carbonNeutralJobs,
  solarPowerJobs,
  energyBusinessJobs,
  energySavingEquipmentJobs,
  circularEconomyJobs,
  chemicalAndPollutionJobs,
  otherEnvironmentalJobs,
} from "../../../utils/constants/constant";
import { getLabelsFromIds } from "../../../utils/helpers/string";

const NotificationSetting: React.FC = () => {
  const { t } = useTranslation();
  const { setStateValue, globalState, setGlobalState, refreshAccessToken } = useGlobalState();
  const { addTimestamp, checkNewsletterFromMailmagId } = useNotificationTimestamp();
  const [state, setState] = useState<INotificationFormState>({
    data: {},
    isSuccess: false,
    accountSF: {},
    dataSyncCustomeSf: {},
  });
  const { data, accountSF, dataSyncCustomeSf } = state;
  const { newsletterSubscription } = data ?? {};
  const { user } = globalState;
  const user_id = user?.user_id;
  const [searchParams] = useSearchParams();
  const serviceId = searchParams.get("serviceid");

  useEffect(() => {
    document.title = "宣伝会議 : アカウント管理 | メールマガジン受信設定";
  }, []);

  useEffect(() => {
    if (user_id) {
      handleFetchUserInfo();
    }
  }, [user_id]);

  const SDKG_InterestingThemes = [
    ...generalSocialTrendsCode,
    ...generalOrganizationAndSkillsKnowledge,
    ...generalWorkAndLifePlanning,
  ];
  SDKG_InterestingThemes.push({ label: "当てはまるものはない", value: "0" });

  const SDKG_EnvironmentalThemes = [
    ...environmentalManagementJobs,
    ...carbonNeutralJobs,
    ...solarPowerJobs,
    ...energyBusinessJobs,
    ...energySavingEquipmentJobs,
    ...circularEconomyJobs,
    ...chemicalAndPollutionJobs,
    ...otherEnvironmentalJobs,
  ]

  const SDKG_NewDepartment = [
    ...businessAndManagementJobs,
    ...creativeAndDesignJobs,
    ...environmentAndInfrastructureJobs,
    ...informationAndTechnologyJobs1,
    ...informationAndTechnologyJobs2,
    ...other,
  ];

  const SDKG_JobChangeInformation = [
    { label: "ある", value: "0" },
    { label: "なし", value: "1" },
  ];

  useEffect(() => {
    if (data) {
      const getLabelFromValue = (value: string) => {
        const found = PERSON_TITLE.find((item) =>
          item?.values?.includes(value)
        );
        return found ? found.label : null;
      };
      setState((prevState) => ({
        ...prevState,
        accountSF: {
          SsoID__pc: user?.user_metadata.customUserId, // customer id
          LastName: data.profile?.fullName?.lastName, // profile.fullname.lastname
          FirstName: data.profile?.fullName?.firstName, // profile.fullname.FirstName
          Company__pc: data.workPlaceInfo?.companyName, // workplace.companyname
          PersonEmail: user?.email,
          SDKG_NewJobLayer__pc:
            positions.find(
              (item) =>
                item.value == data?.workPlaceInfo?.positionCode?.toString()
            )?.label ?? "",
          ContactInformation__pc:
            data.profile?.contactType === 1 ? "自宅" : "勤務先",
          NameKana__pc: `${data?.profile?.fullNameKana?.lastName} ${data?.profile?.fullNameKana?.firstName}`, //profile.fullnameKana.FirstName + profile.fullnameKana.last name
          lbc_office_id__pc: data.workPlaceInfo?.lbcCode ?? "", // lbccode
          DepartmentName__pc: data.workPlaceInfo?.departmentName ?? "", // workplace.DepartmentName
          AddressType__pc: data.profile?.contactType === 1 ? "自宅" : "勤務先", // profile.contactType
          PersonMailingPostalCode:
            data.profile?.contactType !== 1
              ? data.workPlaceInfo?.postalCode?.toString()
              : data.profile?.address?.postalCode?.toString(), // profile.address.PostalCode
          PersonMailingState:
            data.profile?.contactType !== 1
              ? workAddressSelect.find(
                  (item) =>
                    item.value ===
                    data?.workPlaceInfo?.prefectureCode?.toString()
                )?.label ?? ""
              : workAddressSelect.find(
                  (item) =>
                    item.value ===
                    data?.profile?.address?.prefectureCode?.toString()
                )?.label ?? " ", // profile.address.prefectureCode
          PersonMailingCity:
            data.profile?.contactType !== 1
              ? data.workPlaceInfo?.city
              : data.profile?.address?.city, // profile.address.City
          PersonMailingStreet:
            data.profile?.contactType !== 1
              ? data.workPlaceInfo?.line
              : data.profile?.address?.line ?? "", // profile.address.line
          CompanyTel__pc: data.workPlaceInfo?.phoneNumber ?? "", // workplace.phonenumber
          CompanyMobilePhone__pc: data.workPlaceInfo?.mobilePhoneNumber ?? "", // workplace.mobilePhoneNumber
          PersonHomePhone: data.profile?.phoneNumber ?? "", // workplace.phonenumber
          CompanyFax__pc: data.workPlaceInfo?.faxNumber ?? "", // workplace.CompanyFax
          Sex__pc: data.profile?.genderCode?.toString() ?? "0", // profile.genderCode
          TransitionSource__pc: user?.user_metadata.transition_source ?? "", // transition_source
          JobLayer__pc:
            getLabelFromValue(data.workPlaceInfo?.positionCode ?? "") ?? "", // workPlaceInfo.positionCode
          Newsletter1__pc: data.newsletterSubscription?.editorialBooks ?? false, // newsletterSubscription.editorialBooks
          Newsletter3__pc: data.newsletterSubscription?.editorialBrain ?? false, // newsletterSubscription.editorialBrain
          Newsletter5__pc:
            data.newsletterSubscription?.editorialHansokukaigi ?? false, // newsletterSubscription.editorialHansokukaigi
          Newsletter6__pc: data.newsletterSubscription?.advertimes ?? false, // newsletterSubscription.advertimes
          Newsletter7__pc:
            data.newsletterSubscription?.editorialKohokaigi ?? false, // newsletterSubscription.editorialKohokaigi
          Newsletter8__pc: data.newsletterSubscription?.contestsAwards ?? false, // newsletterSubscription.contestsAwards
          Newsletter9__pc:
            data.newsletterSubscription?.editorialSendenkaigi ?? false, // newsletterSubscription.editorialSendenkaigi
          Newsletter24__pc: data.newsletterSubscription?.classInfo ?? false, // newsletterSubscription.classInfo
          Newsletter11__pc:
            data.newsletterSubscription?.digitalMagazine ?? false, // newsletterSubscription.digitalMagazine
          Newsletter25__pc:
            data.newsletterSubscription?.adPlacementSponsorship ?? false, // newsletterSubscription.adPlacementSponsorship
          Newsletter18__pc:
            data.newsletterSubscription?.environmentalBusinessOnline ?? false, // newsletterSubscription.environmentalBusinessOnline
          Newsletter10__pc:
            data.newsletterSubscription?.sendenkaigiInfo ?? false, // newsletterSubscription.sendenkaigiInfo
          Newsletter21__pc:
            data.newsletterSubscription?.environmentalBusinessOnlineNews ?? false,
          Newsletter26__pc:
            data.newsletterSubscription?.jobChangeInfo ?? false, // newsletterSubscription.sendenkaigiInfo
        },
        dataSyncCustomeSf: {
          SsoID__c: user?.user_metadata.customUserId.toString(),
          LastName: data.profile?.fullName?.lastName,
          FirstName: data.profile?.fullName?.firstName,
          NameKana__pc: `${data?.profile?.fullNameKana?.lastName} ${data?.profile?.fullNameKana?.firstName}`,
          Company__pc: data.workPlaceInfo?.companyName,
          PersonDepartment: data.workPlaceInfo?.departmentName,
          SDKG_NewJobLayer__pc:
            positions.find(
              (item) =>
                item.value == data?.workPlaceInfo?.positionCode?.toString()
            )?.label ?? "",
          ContactInformation__pc:
            data.profile?.contactType !== 1 ? "勤務先" : "自宅",
          SDKG_PriorityContacts__pc:
            data.profile?.contactType !== 1 ? "勤務先" : "自宅",
          AddressType__pc: data.profile?.contactType === 1 ? "自宅" : "勤務先",
          PersonMailingPostalCode:
            data.profile?.contactType !== 1
              ? data.workPlaceInfo?.positionCode?.toString()
              : data.profile?.address?.postalCode?.toString(),
          PersonMailingState:
            data.profile?.contactType !== 1
              ? workAddressSelect.find(
                  (item) =>
                    item.value ===
                    data?.workPlaceInfo?.prefectureCode?.toString()
                )?.label ?? ""
              : workAddressSelect.find(
                  (item) =>
                    item.value ===
                    data?.profile?.address?.prefectureCode?.toString()
                )?.label ?? " ",
          PersonMailingCity:
            data.profile?.contactType !== 1
              ? data.workPlaceInfo?.city
              : data.profile?.address?.city,
          PersonMailingStreet:
            data.profile?.contactType !== 1
              ? data.workPlaceInfo?.line
              : data.profile?.address?.line ?? "",
          CompanyTel__pc: data.workPlaceInfo?.phoneNumber ?? "",
          CompanyMobilePhone__pc: data.workPlaceInfo?.mobilePhoneNumber ?? "",
          PersonHomePhone: data.profile?.phoneNumber ?? "",
          PersonMobilePhone: data.profile?.mobilePhoneNumber ?? "",
          CompanyFax__pc: data.workPlaceInfo?.faxNumber ?? "",
          PersonEmail: user?.email,
          TransitionSource__pc: user?.user_metadata.transition_source ?? "", // transition_source
          Sex__pc: data.profile?.genderCode?.toString() ?? "0",
          PersonBirthdate: `${data?.profile?.birth?.year}/${data?.profile?.birth?.month}/${data?.profile?.birth?.day}`,
          JobLayer__pc:
            getLabelFromValue(data.workPlaceInfo?.positionCode ?? "") ?? "",
          lbc_office_id__pc: data.workPlaceInfo?.lbcCode ?? "",
          Newsletter1__pc: data.newsletterSubscription?.editorialBooks ?? false,
          Newsletter3__pc: data.newsletterSubscription?.editorialBrain ?? false,
          Newsletter5__pc:
            data.newsletterSubscription?.editorialHansokukaigi ?? false,
          Newsletter6__pc: data.newsletterSubscription?.advertimes ?? false,
          Newsletter7__pc:
            data.newsletterSubscription?.editorialKohokaigi ?? false,
          Newsletter8__pc: data.newsletterSubscription?.contestsAwards ?? false,
          Newsletter9__pc:
            data.newsletterSubscription?.editorialSendenkaigi ?? false,
          Newsletter24__pc: data.newsletterSubscription?.classInfo ?? false,
          Newsletter11__pc:
            data.newsletterSubscription?.digitalMagazine ?? false,
          Newsletter25__pc:
            data.newsletterSubscription?.adPlacementSponsorship ?? false,
          Newsletter18__pc:
            data.newsletterSubscription?.environmentalBusinessOnline ?? false,
          Newsletter10__pc:
            data.newsletterSubscription?.sendenkaigiInfo ?? false,
          Newsletter26__pc:
            data.newsletterSubscription?.jobChangeInfo ?? false,
          Newsletter21__pc:
            data.newsletterSubscription?.environmentalBusinessOnlineNews ?? false,
          SDKG_InterestingThemes__pc: getLabelsFromIds(
            SDKG_InterestingThemes,
            data?.interest?.jobCodes ?? []
          ),
          SDKG_NewDepartment__pc: getLabelsFromIds(
            SDKG_NewDepartment,
            data.workPlaceInfo?.affiliationCodes ?? []
          ),
          SDKG_Occupation__pc: occupationSelect.find(
            (item) => item.value === data?.profile?.occupationCode?.toString()
          )?.label,
          SDKG_EnvironmentalThemes__pc: getLabelsFromIds(
            SDKG_EnvironmentalThemes,
            data?.interest?.environmentBusinessCodes ?? []
          ),
          SDKG_JobChangeInformation__pc: SDKG_JobChangeInformation.find(
            (item) => item.value === data?.interest?.jobChangeCode?.toString()
          )?.label ?? "",
        },
      }));
    }
  }, [data]);

  // MEMO: 1 or 3でこの画面に来ることはないので、多分不要な処理
  useEffect(() => {
    if (
      (user?.user_metadata.user_status === 1 || user?.user_metadata.user_status === 3) &&
      data?.transition_source === "kankyobusiness"
    ) {
      setState((prevState) => ({
        ...prevState,
        data: {
          ...prevState?.data,
          newsletterSubscription: {
            ...(prevState?.data?.newsletterSubscription ?? {}),
            environmentalBusinessOnline: true,
            environmentalBusinessOnlineNews: true,
          },
        },
      }));
    }
  }, [data.transition_source]);

  const handleChange =
    (field: string) => (event: React.ChangeEvent<HTMLInputElement>) => {
      const checked = event.target.checked;
      setState((prevState) => ({
        ...prevState,
        data: {
          ...prevState?.data,
          newsletterSubscription: {
            ...(prevState?.data?.newsletterSubscription ?? {}),
            [field]: checked,
          }
        },
      }));
    };

  const handleScrollToTop = () => {
    document.documentElement.style.scrollBehavior = "auto";
    setTimeout(() => window.scrollTo(0, 0), 5);
    setTimeout(
      () => (document.documentElement.style.scrollBehavior = "smooth"),
      100
    );
  };

  const handleFetchUserInfo = async () => {
    setStateValue("loader", true);
    try {
      // const result = await fetchUserInfo(user_id ?? "");
      const { user_metadata } = globalState.user
      if (user_metadata) {
        let dataToSet:  IUserMetaDataApi = { ...user_metadata };
        const mailMagId = localStorage.getItem("mailmagid");
        if (mailMagId) {
          dataToSet = checkNewsletterFromMailmagId(mailMagId, dataToSet);
        }

        setState((prevState) => ({
          ...prevState,
          data: dataToSet,
        }));
      }
    } catch (err) {
      if (!(err instanceof Error)) {
        const res = err as AxiosResponse;
        return res;
      }
    } finally {
      setStateValue("loader", false);
    }
  };

  // 途中離脱を防止するため1つのAPIで処理をする
  const handleUpsertAccountAndSyncSF = async () => {
    try {
      await upsertAccountAndSyncSF(
        {
          SsoID__pc: user?.user_metadata.customUserId,
          Company__pc: user?.user_metadata.workPlaceInfo?.companyName,
          PersonEmail: user?.email
        },
        accountSF ?? {},
        {

          SsoID__c: user?.user_metadata.customUserId.toString(),
          LastName: user?.user_metadata.profile?.fullName?.lastName,
          FirstName: user?.user_metadata.profile?.fullName?.firstName,
          Company__pc: user?.user_metadata.workPlaceInfo?.companyName,
          PersonEmail: user?.email
        },
        accountSF ?? {},
        dataSyncCustomeSf ?? {}
      )
    } catch (error) {
      throw error;
    }
  }

  const handleSubmit = async () => {
    setStateValue("isSaving", true);
    try {
      const originalUser = globalState.user?.user_metadata || {};
      const dataWithTimestamp = addTimestamp(originalUser, data);
      const result = await fetchUpdateUserMeataData(user_id ?? "", dataWithTimestamp);
      if (result?.status === Http.SUCCESS_CODE) {
        handleScrollToTop();
        setGlobalState({
          ...globalState,
          loader: true,
          user: {
            ...globalState.user,
            user_metadata: dataWithTimestamp
          }
        });
        setState((prevState) => ({
          ...prevState,
          isSuccess: true,
        }));
      }
      
      await refreshAccessToken();
      await handleUpsertAccountAndSyncSF();
      
    } catch (err) {
      if (!(err instanceof Error)) {
        const res = err as AxiosResponse;
        return res;
      }
    } finally {
      setStateValue("isSaving", false);
    }
  };

  return (
    <>
      {serviceId === "7" || serviceId === "8" ? (
        <p>"このページにアクセスする権限がありません。"</p>
      ) : (
        <Box className="page__notification">
          <h1 className="header-tit">{t("features.notification.headerTit")}</h1>
          {state.isSuccess && (
            <div className="mb-3">
              <Alert
                sx={{
                  bgcolor: "#dff0d8",
                  color: "#3c763d",
                }}
                icon={false}
                severity="success"
                onClose={() => {
                  setState((prevState) => ({
                    ...prevState,
                    isSuccess: false,
                  }));
                }}
              >
                {t("features.notification.setupCompleted")}
              </Alert>
            </div>
          )}
          <p className="sub-title">
            配信を希望するメールマガジンを選択してください
          </p>
          <Box>
            <h3 className="tit-buisness-category">
              {t("features.notification.kaigiHotTopics")}
            </h3>
            <ul className="checkbox-list">
              <li>
                <FormControlLabel
                  control={
                    <Checkbox
                      name="sendenkaigiInfo"
                      onChange={handleChange("sendenkaigiInfo")}
                      // value={1}
                      checked={newsletterSubscription?.sendenkaigiInfo ?? false}
                      sx={{
                        "& .MuiSvgIcon-root": {
                          fontSize: "16px",
                        },
                      }}
                      size="small"
                    />
                  }
                  label={t("features.notification.sendenkaigiInfo")}
                  className="form-label-item"
                />
              </li>
              <li>
                <FormControlLabel
                  control={
                    <Checkbox
                      name="classInfo"
                      onChange={handleChange("classInfo")}
                      // value={2}
                      checked={newsletterSubscription?.classInfo ?? false}
                      sx={{
                        "& .MuiSvgIcon-root": {
                          fontSize: "16px",
                        },
                      }}
                      size="small"
                    />
                  }
                  label={t("features.notification.classInfo")}
                  className="form-label-item"
                />
              </li>
              <li>
                <FormControlLabel
                  control={
                    <Checkbox
                      name="contestsAwards"
                      onChange={handleChange("contestsAwards")}
                      // value={5}
                      checked={newsletterSubscription?.contestsAwards ?? false}
                      sx={{
                        "& .MuiSvgIcon-root": {
                          fontSize: "16px",
                        },
                      }}
                      size="small"
                    />
                  }
                  label={t("features.notification.contestsAwards")}
                  className="form-label-item"
                />
              </li>
            </ul>

            <h3 className="tit-buisness-category">編集部からのBreaking News</h3>
            <ul className="checkbox-list">
              <li>
                <FormControlLabel
                  control={
                    <Checkbox
                      name="notification_id"
                      onChange={handleChange("advertimes")}
                      // value={14}
                      checked={newsletterSubscription?.advertimes ?? false}
                      sx={{
                        "& .MuiSvgIcon-root": {
                          fontSize: "16px",
                        },
                      }}
                      size="small"
                    />
                  }
                  label={t("features.notification.adverTimesCheckBox")}
                  className="form-label-item"
                />
              </li>
              <li>
                <FormControlLabel
                  control={
                    <Checkbox
                      name="digitalMagazine"
                      onChange={handleChange("digitalMagazine")}
                      // value={15}
                      checked={newsletterSubscription?.digitalMagazine ?? false}
                      sx={{
                        "& .MuiSvgIcon-root": {
                          fontSize: "16px",
                        },
                      }}
                      size="small"
                    />
                  }
                  label={t("features.notification.magazineCheckBox")}
                  className="form-label-item"
                />
              </li>
              <li>
                <FormControlLabel
                  control={
                    <Checkbox
                      name="editorialSendenkaigi"
                      onChange={handleChange("editorialSendenkaigi")}
                      // value={8}
                      checked={
                        newsletterSubscription?.editorialSendenkaigi ?? false
                      }
                      sx={{
                        "& .MuiSvgIcon-root": {
                          fontSize: "16px",
                        },
                      }}
                      size="small"
                    />
                  }
                  label={t("features.notification.editorialSendenkaigi")}
                  className="form-label-item"
                />
              </li>
              <li>
                <FormControlLabel
                  control={
                    <Checkbox
                      name="editorialHansokukaigi"
                      onChange={handleChange("editorialHansokukaigi")}
                      // value={10}
                      checked={
                        newsletterSubscription?.editorialHansokukaigi ?? false
                      }
                      sx={{
                        "& .MuiSvgIcon-root": {
                          fontSize: "16px",
                        },
                      }}
                      size="small"
                    />
                  }
                  label={t("features.notification.editorialHansokukaigi")}
                  className="form-label-item"
                />
              </li>
              <li>
                <FormControlLabel
                  control={
                    <Checkbox
                      name="editorialKohokaigi"
                      onChange={handleChange("editorialKohokaigi")}
                      // value={11}
                      checked={
                        newsletterSubscription?.editorialKohokaigi ?? false
                      }
                      sx={{
                        "& .MuiSvgIcon-root": {
                          fontSize: "16px",
                        },
                      }}
                      size="small"
                    />
                  }
                  label={t("features.notification.editorialKohokaigi")}
                  className="form-label-item"
                />
              </li>
              <li>
                <FormControlLabel
                  control={
                    <Checkbox
                      name="editorialBrain"
                      onChange={handleChange("editorialBrain")}
                      // value={9}
                      checked={newsletterSubscription?.editorialBrain ?? false}
                      sx={{
                        "& .MuiSvgIcon-root": {
                          fontSize: "16px",
                        },
                      }}
                      size="small"
                    />
                  }
                  label={t("features.notification.editorialBrain")}
                  className="form-label-item"
                />
              </li>
              <li>
                <FormControlLabel
                  control={
                    <Checkbox
                      name="editorialBooks"
                      onChange={handleChange("editorialBooks")}
                      // value={7}
                      checked={newsletterSubscription?.editorialBooks ?? false}
                      sx={{
                        "& .MuiSvgIcon-root": {
                          fontSize: "16px",
                        },
                      }}
                      size="small"
                    />
                  }
                  label={t("features.notification.editorialBooks")}
                  className="form-label-item"
                />
              </li>
            </ul>

            <h3 className="tit-buisness-category">環境ビジネス</h3>
            <ul className="checkbox-list">
              <li>
                <FormControlLabel
                  control={
                    <Checkbox
                      name="environmentalBusinessOnline"
                      onChange={handleChange("environmentalBusinessOnline")}
                      // value={7}
                      checked={newsletterSubscription?.environmentalBusinessOnline ?? false}
                      sx={{
                        "& .MuiSvgIcon-root": {
                          fontSize: "16px",
                        },
                      }}
                      size="small"
                    />
                  }
                  label={t("features.notification.environmentalBusinessOnline")}
                  className="form-label-item"
                />
              </li>
              <li>
                <FormControlLabel
                  control={
                    <Checkbox
                      name="environmentalBusinessOnlineNews"
                      onChange={handleChange("environmentalBusinessOnlineNews")}
                      // value={7}
                      checked={newsletterSubscription?.environmentalBusinessOnlineNews ?? false}
                      sx={{
                        "& .MuiSvgIcon-root": {
                          fontSize: "16px",
                        },
                      }}
                      size="small"
                    />
                  }
                  label={t("features.notification.environmentalBusinessOnlineNews")}
                  className="form-label-item"
                />
              </li>
            </ul>

            <h3 className="tit-buisness-category">Additional Information</h3>
            <ul className="checkbox-list">
              <li>
                <FormControlLabel
                  control={
                    <Checkbox
                      name="adPlacementSponsorship "
                      onChange={handleChange("adPlacementSponsorship")}
                      // value={16}
                      checked={
                        newsletterSubscription?.adPlacementSponsorship ?? false
                      }
                      sx={{
                        "& .MuiSvgIcon-root": {
                          fontSize: "16px",
                        },
                      }}
                      size="small"
                    />
                  }
                  label={t("features.notification.adPlacementSponsorship")}
                  className="form-label-item"
                />
              </li>
            </ul>
            <Box className="center mt-4">
              <Button
                disabled={globalState.isSaving}
                onClick={handleSubmit}
                className="btn btn-primary form-control btn-md submit"
              >
                適用
              </Button>
            </Box>
          </Box>
        </Box>
      )}
    </>
  );
};

export default NotificationSetting;
